import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RoleGate } from '@/features/auth/RoleGate';
import { supabase } from '@/lib/supabase';
import {
  testPlatformConnection,
  fetchPlatformAgents,
  getPlatformUrl,
} from '@/lib/kaycoo-platform';

export function SettingsPage() {
  return (
    <RoleGate minimumRole="admin" fallback={<p className="py-16 text-center text-muted-foreground">Admin access required</p>}>
      <PageHeader title="Settings" description="Manage AI configuration, API keys, and users." />

      <Tabs defaultValue="ai">
        <TabsList>
          <TabsTrigger value="ai">AI Agents</TabsTrigger>
          <TabsTrigger value="keys">API Keys</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-6">
          <AgentsTab />
        </TabsContent>

        <TabsContent value="keys" className="mt-6">
          <ApiKeysTab />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <UsersTab />
        </TabsContent>
      </Tabs>
    </RoleGate>
  );
}

/* ---- AI Agents Tab ---- */

function AgentsTab() {
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'checking' | 'connected' | 'failed'>('idle');

  const { data: platformAgents, isLoading, refetch } = useQuery({
    queryKey: ['platform-agents'],
    queryFn: fetchPlatformAgents,
  });

  const checkConnection = async () => {
    setConnectionStatus('checking');
    const ok = await testPlatformConnection();
    setConnectionStatus(ok ? 'connected' : 'failed');
    if (ok) refetch();
  };

  return (
    <div className="space-y-4">
      {/* Platform connection */}
      <Card>
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <p className="text-sm font-medium">Kaycoo Agentic Platform</p>
            <p className="text-xs text-muted-foreground">{getPlatformUrl()}</p>
          </div>
          <div className="flex items-center gap-2">
            {connectionStatus === 'connected' && <Badge variant="success">Connected</Badge>}
            {connectionStatus === 'failed' && <Badge variant="destructive">Unreachable</Badge>}
            <Button variant="outline" size="sm" onClick={checkConnection} disabled={connectionStatus === 'checking'}>
              {connectionStatus === 'checking' ? 'Checking...' : 'Test Connection'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Agents from platform */}
      {isLoading && [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}

      {!isLoading && (platformAgents ?? []).length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No agents found on the platform for app_id &quot;koip&quot;.
            Register agents on the Kaycoo Agentic Platform to see them here.
          </CardContent>
        </Card>
      )}

      {(platformAgents ?? []).map((agent) => (
        <Card key={agent.assistant_id}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">{agent.name}</CardTitle>
            <Badge variant={agent.status === 'active' ? 'success' : 'secondary'}>
              {agent.status}
            </Badge>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{agent.description}</p>
            <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
              <span>Model: {agent.model_id}</span>
              <span>Provider: {agent.provider}</span>
              {agent.agent_id && <span>ID: {agent.agent_id}</span>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---- API Keys Tab ---- */

interface ApiKey {
  id: string;
  provider: string;
  key_encrypted: string;
  created_at: string;
}

function ApiKeysTab() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [keyValue, setKeyValue] = useState('');
  const [platformStatus, setPlatformStatus] = useState<'idle' | 'checking' | 'connected' | 'failed'>('idle');

  const { data: keys, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const { data } = await supabase
        .from('api_key')
        .select('id, provider, key_encrypted, created_at')
        .order('provider');
      return (data ?? []) as unknown as ApiKey[];
    },
  });

  const addKey = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from('api_key').insert({
        provider: serviceName,
        key_encrypted: keyValue,
        created_by: user.id,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setShowAdd(false);
      setServiceName('');
      setKeyValue('');
    },
  });

  const deleteKey = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('api_key').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const checkPlatform = async () => {
    setPlatformStatus('checking');
    const ok = await testPlatformConnection();
    setPlatformStatus(ok ? 'connected' : 'failed');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">API Keys</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={checkPlatform} disabled={platformStatus === 'checking'}>
            {platformStatus === 'checking' ? 'Checking...' : 'Test Platform'}
          </Button>
          {platformStatus === 'connected' && <Badge variant="success">Connected</Badge>}
          {platformStatus === 'failed' && <Badge variant="destructive">Failed</Badge>}
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Key
          </Button>
        </div>
      </div>

      {isLoading && <Skeleton className="h-20 w-full" />}

      {(keys ?? []).length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground text-sm">
            No API keys configured. Add keys for Kaycoo Platform, Brave Search, and ElevenLabs.
          </CardContent>
        </Card>
      )}

      {(keys ?? []).map((k) => (
        <Card key={k.id}>
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium">{k.provider}</p>
              <p className="text-xs text-muted-foreground font-mono">{k.key_encrypted.slice(0, 4)}...{k.key_encrypted.slice(-4)}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => deleteKey.mutate(k.id)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </CardContent>
        </Card>
      ))}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add API Key</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={serviceName} onValueChange={setServiceName}>
                <SelectTrigger><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="kaycoo_platform">Kaycoo Platform</SelectItem>
                  <SelectItem value="brave_search">Brave Search</SelectItem>
                  <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Key Value</Label>
              <Input type="password" value={keyValue} onChange={(e) => setKeyValue(e.target.value)} placeholder="Enter API key" />
            </div>
            <Button onClick={() => addKey.mutate()} disabled={!serviceName || !keyValue || addKey.isPending} className="w-full">
              {addKey.isPending ? 'Saving...' : 'Save Key'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- Users Tab ---- */

interface TenantUser {
  id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'manager' | 'employee';
  avatar_url: string | null;
}

function UsersTab() {
  const queryClient = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('employee');

  const { data: users, isLoading } = useQuery({
    queryKey: ['tenant-users'],
    queryFn: async () => {
      const { data } = await supabase
        .from('profile')
        .select('id, full_name, email, role, avatar_url')
        .order('full_name');
      return (data ?? []) as unknown as TenantUser[];
    },
  });

  const inviteUser = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.functions.invoke('auth-invite', {
        body: { email: inviteEmail, role: inviteRole },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('employee');
    },
  });

  const updateRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase.functions.invoke('auth-role', {
        body: { user_id: userId, role },
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-users'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Team Members</h3>
        <Button size="sm" onClick={() => setShowInvite(true)}>
          <UserPlus className="mr-2 h-4 w-4" /> Invite User
        </Button>
      </div>

      {isLoading && <Skeleton className="h-20 w-full" />}

      {(users ?? []).map((user) => (
        <Card key={user.id}>
          <CardContent className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium">
                {(user.full_name ?? user.email)?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium">{user.full_name || user.email}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </div>
            </div>
            <Select
              value={user.role}
              onValueChange={(role) => updateRole.mutate({ userId: user.id, role })}
            >
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="employee">Employee</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      ))}

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="user@kaycoo.fi" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => inviteUser.mutate()} disabled={!inviteEmail || inviteUser.isPending} className="w-full">
              {inviteUser.isPending ? 'Sending...' : 'Send Invite'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
